// PARAQTA — Step BP4-rev: Auto-approve кітап ұсыну + admin recent
// additions panel + Top Nav admin shield button.
//
// Workflow:
//   Catalog → "Кітабым жоқ" → submit modal (title type-ahead).
//   Submit → POST /api/books/submit → MySQL books-ке INSERT (auto) →
//     каталогта бірден көрінеді → openBookPage(slug) navigation.
//   Admin /#admin → recent 50 books (filter: user/bot/all) → delete
//     button → confirmation → DELETE.
//
// Spam: айлық 5 шек (backend 429); type-ahead дубль қорғайды.

import { api } from '../../core/api.js';
import { esc } from '../../utils/format.js';
import { toast } from '../../utils/toast.js';
import { coverBox } from '../../utils/covers.js';
import { showLoginModal } from '../auth/login-modal.js';

const GENRE_OPTIONS = [
  { v: 'korkem',   l: '📖 Көркем әдебиет' },
  { v: 'tarix',    l: '📜 Тарих' },
  { v: 'filos',    l: '🧠 Философия' },
  { v: 'psix',     l: '🧠 Психология' },
  { v: 'poeziia',  l: '✍ Поэзия' },
  { v: 'biograf',  l: '👤 Биография' },
  { v: 'din',      l: '🕌 Дін' },
  { v: 'biznes',   l: '💼 Бизнес' },
  { v: 'balalar',  l: '🧒 Балалар' },
  { v: 'gylym',    l: '🔬 Ғылым' },
];
const LANG_OPTIONS = [
  { v: 'kk', l: 'Қазақша' },
  { v: 'ru', l: 'Орысша' },
  { v: 'en', l: 'English' },
];

// Type-ahead debounce (ms). 300ms — UI үлгісі, server-ге тым жиі сұрау.
const TYPEAHEAD_DEBOUNCE = 300;
const TYPEAHEAD_MIN_CHARS = 3;

// Config — main.js startup-та + auth/index.js loginDone() қайта шақырады.
export async function loadAppConfig() {
  try {
    const cfg = await api('/api/config');
    window.appConfig = cfg || {};
    document.body.classList.toggle('admin-user', !!cfg.is_admin);
    document.body.classList.toggle('submissions-on', !!cfg.allow_user_submissions);
    document.body.classList.toggle('bookshelf-on', !!cfg.bookshelf_enabled);
    document.body.classList.toggle('premium-on', !!cfg.premium_site_enabled);
  } catch {
    window.appConfig = {};
  }
}

// ── Submit modal ──
export function showSubmitModal() {
  if (!window.currentUser) { showLoginModal(); return; }
  if (!window.appConfig?.allow_user_submissions) {
    toast('Кітап ұсыну уақытша өшірілген');
    return;
  }
  const modal = document.getElementById('submitModal');
  if (!modal) return;
  modal.querySelector('.submit-modal-card').innerHTML =
    '<button class="submit-modal-close" data-action="close-submit-modal">×</button>' +
    '<div class="submit-modal-title">Кітап енгізу</div>' +
    '<div class="submit-modal-desc">Кітапханада жоқ кітап? Енгізіп көр — кітапханада пайда болады.</div>' +
    '<form id="submitForm" data-action="_stop" autocomplete="off">' +
      // Title field has its own block with suggestions container under it.
      '<label class="submit-modal-field submit-modal-title-field">' +
        '<span>Кітап атауы *</span>' +
        '<input name="title" type="text" maxlength="200" id="submitTitleInput" autocomplete="off">' +
        '<div class="submit-typeahead" id="submitTypeahead"></div>' +
      '</label>' +
      field('author', 'Автор *', 'text', { maxlength: 100 }) +
      selectField('genre', 'Жанр *', GENRE_OPTIONS) +
      selectField('language', 'Тіл *', LANG_OPTIONS) +
      field('pages', 'Бет саны *', 'number', { min: 10, max: 2000 }) +
      field('isbn', 'ISBN (міндетті emес)', 'text', { maxlength: 20 }) +
      textareaField('description', 'Қысқаша сипаттама (міндетті emес)', 400) +
      '<button type="button" class="submit-modal-btn" data-action="submit-book">Енгізу</button>' +
    '</form>';
  modal.classList.add('open');
  setupTypeahead();
}

function field(name, label, type, attrs) {
  attrs = attrs || {};
  let extra = '';
  for (const k of Object.keys(attrs)) extra += ' ' + k + '="' + attrs[k] + '"';
  return '<label class="submit-modal-field">' +
    '<span>' + esc(label) + '</span>' +
    '<input name="' + name + '" type="' + type + '"' + extra + '>' +
    '</label>';
}
function selectField(name, label, options) {
  return '<label class="submit-modal-field">' +
    '<span>' + esc(label) + '</span>' +
    '<select name="' + name + '">' +
      options.map(o => '<option value="' + o.v + '">' + esc(o.l) + '</option>').join('') +
    '</select></label>';
}
function textareaField(name, label, maxlength) {
  return '<label class="submit-modal-field">' +
    '<span>' + esc(label) + '</span>' +
    '<textarea name="' + name + '" maxlength="' + maxlength + '" rows="3"></textarea>' +
    '</label>';
}

// Type-ahead. Title енгізген кезде debounced GET /api/books/search.
// 3+ char → suggestions dropdown. Click → close modal + openBookPage(slug).
// No results → hint "Жоқ болса — енгізе беруге болады".
function setupTypeahead() {
  const input = document.getElementById('submitTitleInput');
  const dropdown = document.getElementById('submitTypeahead');
  if (!input || !dropdown) return;
  let timer = null;

  const fetchSuggestions = async () => {
    const term = input.value.trim();
    if (term.length < TYPEAHEAD_MIN_CHARS) {
      dropdown.innerHTML = '';
      dropdown.classList.remove('open');
      return;
    }
    try {
      const url = '/api/books/search?q_param=' + encodeURIComponent(term);
      const items = await api(url);
      renderSuggestions(items, dropdown);
    } catch {
      // Silent — type-ahead optional UX.
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(fetchSuggestions, TYPEAHEAD_DEBOUNCE);
  });
}

function renderSuggestions(items, dropdown) {
  if (!items || !items.length) {
    dropdown.innerHTML =
      '<div class="submit-typeahead-empty">Жоқ — енгізе беруге болады.</div>';
    dropdown.classList.add('open');
    return;
  }
  dropdown.innerHTML = items.map(b =>
    '<button type="button" class="submit-typeahead-item" data-action="open-typeahead" data-slug="' + esc(b.slug) + '">' +
      '<div class="submit-typeahead-cover">' + coverBox(b.title, b.author, b.cover_url) + '</div>' +
      '<div class="submit-typeahead-text">' +
        '<div class="submit-typeahead-title">' + esc(b.title) + '</div>' +
        '<div class="submit-typeahead-author">' + esc(b.author || '') + '</div>' +
      '</div>' +
    '</button>'
  ).join('');
  dropdown.classList.add('open');
}

// Type-ahead суggestion-ке басу: modal жабу + openBookPage(slug).
export function openTypeaheadResult(slug) {
  if (!slug) return;
  closeSubmitModal();
  // openBookPage main.js-те window-да экспозылған.
  if (typeof window.openBookPage === 'function') {
    window.openBookPage(slug);
  } else {
    location.hash = 'book/' + slug;
  }
}

export function closeSubmitModal() {
  document.getElementById('submitModal')?.classList.remove('open');
}

export async function submitBook() {
  const form = document.getElementById('submitForm');
  if (!form) return;
  const fd = new FormData(form);
  const body = Object.fromEntries(fd.entries());
  body.pages = parseInt(body.pages, 10) || 0;
  if (!body.title || body.title.length < 2) { toast('Кітап атауын енгіз'); return; }
  if (!body.author || body.author.length < 2) { toast('Автор атын енгіз'); return; }
  if (body.pages < 10 || body.pages > 2000) { toast('Бет саны 10–2000 аралығында'); return; }

  const btn = form.querySelector('[data-action="submit-book"]');
  if (btn) { btn.disabled = true; btn.textContent = 'Жіберілуде...'; }

  try {
    const r = await fetch('/api/books/submit', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      const d = await r.json();
      toast('✓ Кітап каталогта');
      closeSubmitModal();
      // Auto-approve → бірден slug-ке navigate.
      if (d.slug && typeof window.openBookPage === 'function') {
        window.openBookPage(d.slug);
      }
    } else if (r.status === 429) {
      toast('⚠️ Айлық 5 кітап шек жетті');
      if (btn) { btn.disabled = false; btn.textContent = 'Енгізу'; }
    } else {
      const err = await r.json().catch(() => ({}));
      toast('Қате: ' + (err.detail || r.status));
      if (btn) { btn.disabled = false; btn.textContent = 'Енгізу'; }
    }
  } catch {
    toast('Желі қатесі');
    if (btn) { btn.disabled = false; btn.textContent = 'Енгізу'; }
  }
}

// ── Admin panel: recent additions ──

// Active filter — "all" | "user" | "bot". Persists across re-renders.
let _adminFilter = 'all';

// ── Админ панель: «саған дәл қазір не керек» ────────────────────
// Бұрын 6 блок ҚАТАР жүктелетін де, бір ұзын скроллда тұратын: founder бір
// төлемді растау үшін кірсе де, блогер/квиз/марафонның бәрін айналып өтетін.
// Енді: жоғарыда кезек (нені растау керек), астында БАСҚАНДА ашылатын
// бөлімдер. Ештеңе күтіп тұрмаса — «бәрі таза» деп жазылады.
const SECTIONS = [
  { key: 'stats',    icon: '📊', title: 'Статистика', render: renderStats,          wrap: 'adminStatsWrap' },
  { key: 'pay',      icon: '💰', title: 'Төлемдер',   render: renderPremiumOrders,  wrap: 'adminPremiumWrap' },
  { key: 'library',  icon: '📚', title: 'Кітапхана',  render: renderLibraryAdmin,   wrap: 'adminLibraryWrap' },
  { key: 'marathon', icon: '🏁', title: 'Марафон',    render: renderMarathonOrders, wrap: 'adminMarathonWrap' },
  { key: 'quiz',     icon: '❓', title: 'Квиз',       render: renderQuizBoth,       wrap: 'adminQuizWrapAll' },
  { key: 'blogger',  icon: '📣', title: 'Блогерлер',  render: renderBloggers,       wrap: 'adminBloggerWrap' },
  { key: 'schools',  icon: '🏛', title: 'Мектептер',  render: renderSchoolsAdmin,   wrap: 'adminSchoolsWrap' },
  { key: 'circle',   icon: '🏫', title: 'Шеңбер рұқсаты', render: renderCircleAccess, wrap: 'adminCircleWrap' },
  { key: 'books',    icon: '🗂', title: 'Соңғы кітаптар', render: renderRecentBlock, wrap: 'adminRecentWrap' },
];
const _opened = {};

// ── 📊 Статистика (13.10 founder: «өзім қалаған уақытта статистика көретін жер жоқ») ──────────
// /api/admin/stats — күн сайынғы сандар. Анықтамалар дүйсенбілік есептегідей (бет = бот+сайт жиынтығы).
const STATS_CSS = `
.st-chips{display:flex;gap:6px;margin:4px 0 12px}.st-chips button{flex:1;min-height:38px;border-radius:999px;border:1px solid var(--line,#ddd);background:transparent;color:inherit;font:600 13px inherit;cursor:pointer}
.st-chips button.on{background:var(--ink,#222);color:var(--bg,#fff);border-color:var(--ink,#222)}
.st-cards{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin-bottom:12px}
.st-card{border:1px solid var(--line,#ddd);border-radius:14px;padding:10px 12px}.st-card b{display:block;font:700 22px/1.1 "PT Serif",Georgia,serif;font-variant-numeric:tabular-nums}
.st-card span{font-size:12px;opacity:.7}.st-card i{font-style:normal;font-size:11.5px;opacity:.7;display:block;margin-top:2px}
.st-k{font:600 10.5px "IBM Plex Mono",monospace;letter-spacing:.14em;text-transform:uppercase;opacity:.6;margin:14px 0 6px}
.st-chart{display:flex;align-items:flex-end;gap:2px;height:120px;padding:6px 0;border-bottom:1px solid var(--line,#ddd)}
.st-chart i{flex:1;background:#E9A949;border-radius:3px 3px 0 0;min-height:2px;position:relative}.st-chart i.t{background:#C2551E}
.st-tw{overflow-x:auto;margin-top:10px}.st-t{width:100%;border-collapse:collapse;font-size:12.5px;font-variant-numeric:tabular-nums;white-space:nowrap}
.st-t th,.st-t td{padding:6px 7px;text-align:right;border-bottom:1px solid var(--line,#eee)}.st-t th:first-child,.st-t td:first-child{text-align:left}
.st-t th{font-weight:600;font-size:11px;opacity:.7}.st-note{font-size:11.5px;opacity:.65;margin-top:8px;line-height:1.45}`;
let _statsDays = 30;
async function renderStats() {
  const w = document.getElementById('adminStatsWrap');
  if (!w) return;
  if (!document.getElementById('st-css')) { const c = document.createElement('style'); c.id = 'st-css'; c.textContent = STATS_CSS; document.head.appendChild(c); }
  let d = null;
  try { d = await api(`/api/admin/stats?days=${_statsDays}`); } catch (_) {}
  if (!d || !d.ok) { w.innerHTML = '<div class="admin-empty">Статистика жүктелмеді</div>'; return; }
  const nf = v => new Intl.NumberFormat('ru-RU').format(Math.round(v || 0));
  const days = d.days || [], t = days[0] || {}, y = days[1] || {};
  const sum = k => days.reduce((a, x) => a + (x[k] || 0), 0);
  const diff = (a, b) => (b ? `${a >= b ? '▲' : '▼'} кеше ${nf(b)}` : '');
  const card = (v, label, sub) => `<div class="st-card"><b>${v}</b><span>${label}</span>${sub ? `<i>${sub}</i>` : ''}</div>`;
  const maxP = Math.max(1, ...days.map(x => x.pages || 0));
  const rows = days.map(x => `<tr><td>${x.d.slice(5).split('-').reverse().join('.')}</td><td>${nf(x.visitors)}</td><td>${nf(x.logged)}</td><td>${nf(x.readers)}</td><td>${nf(x.pages)}</td><td>${(x.minutes / 60).toFixed(1)}</td><td>${nf(x.sessions)}</td><td>${nf(x.new_users)}</td><td>${nf(x.abai)}</td><td>${nf(x.finished)}</td><td>${x.revenue ? nf(x.revenue) + ' ₸' : '—'}</td></tr>`).join('');
  w.innerHTML = `
    <div class="st-chips">${[7, 30, 90].map(n => `<button type="button" data-st-days="${n}" class="${n === _statsDays ? 'on' : ''}">${n} күн</button>`).join('')}</div>
    <div class="st-k">Бүгін · ${d.generated || ''}</div>
    <div class="st-cards">
      ${card(nf(t.visitors), 'сайтқа кірген адам', diff(t.visitors, y.visitors))}
      ${card(nf(t.readers), 'оқыған адам', diff(t.readers, y.readers))}
      ${card(nf(t.pages), 'оқылған бет', diff(t.pages, y.pages))}
      ${card(nf(t.new_users), 'жаңа тіркелу', diff(t.new_users, y.new_users))}
    </div>
    <div class="st-k">Соңғы ${days.length} күн</div>
    <div class="st-cards">
      ${card(nf(sum('pages')), 'бет оқылды', `күніне ~${nf(sum('pages') / Math.max(1, days.length))}`)}
      ${card(nf(sum('new_users')), 'жаңа тіркелу', `күніне ~${nf(sum('new_users') / Math.max(1, days.length))}`)}
      ${card(nf(sum('minutes') / 60), 'сағат оқылды (сайт)', `${nf(sum('sessions'))} сессия`)}
      ${card(sum('revenue') ? nf(sum('revenue')) + ' ₸' : '—', 'кіріс', `${nf(sum('finished'))} кітап аяқталды (сайт)`)}
    </div>
    ${d.total_pages != null ? `<div class="st-cards">${card(nf(d.total_pages), 'барлық уақытта бет')}${card(nf(d.total_users), 'барлық қолданушы')}</div>` : ''}
    <div class="st-k">Күн сайын оқылған бет</div>
    <div class="st-chart" role="img" aria-label="Күн сайын оқылған бет">${days.slice().reverse().map((x, i, a) => `<i class="${i === a.length - 1 ? 't' : ''}" style="height:${Math.round((x.pages || 0) / maxP * 100)}%" title="${x.d}: ${nf(x.pages)} бет"></i>`).join('')}</div>
    <div class="st-tw"><table class="st-t"><thead><tr><th>Күн</th><th>Кірген</th><th>Логин</th><th>Оқыған</th><th>Бет</th><th>Сағат</th><th>Сессия</th><th>Жаңа</th><th>Абай</th><th>Аяқт.</th><th>Кіріс</th></tr></thead><tbody>${rows}</tbody></table></div>
    <div class="st-note"><b>Кірген</b>: сайтты/қолданбаны ашқан бірегей адам (қонақ та). <b>Логин</b>: кірген аккаунт. <b>Оқыған</b>: ботта не сайтта бет белгілеген не 1+ минут оқыған. <b>Бет</b>: бот пен сайттың жиынтығы. <b>Сағат, сессия, аяқталған</b>: тек сайт (ботта таймер жоқ). Сандар 5 минут сайын жаңарады.</div>`;
  w.querySelectorAll('[data-st-days]').forEach(b => { b.onclick = () => { _statsDays = Number(b.dataset.stDays); w.innerHTML = '<div class="loading"><span class="spinner"></span>Жүктелуде…</div>'; renderStats(); }; });
}

async function renderQuizBoth() {
  const w = document.getElementById('adminQuizWrapAll');
  if (!w) return;
  w.innerHTML = '<div id="adminQuizUploadWrap"></div><div id="adminQuizWrap"></div>';
  await renderQuizUpload();
  await renderQuizAdmin();
}

// Кітапхана — қорды БАСҚАРУ. Бұрын панельде мүлдем болмаған: кітап қосу
// тек SSH арқылы істелетін. Енді founder өзі қоса алады.
async function renderLibraryAdmin() {
  const w = document.getElementById('adminLibraryWrap');
  if (!w) return;
  let books = [];
  try {
    const d = await api('/api/library/books');
    books = (d && d.books) || [];
  } catch (_) {}
  const byGenre = {};
  books.forEach(b => { (byGenre[b.genre || '—'] = byGenre[b.genre || '—'] || []).push(b); });

  w.innerHTML =
    '<form id="admLibForm" class="adm-form">' +
      '<div class="adm-fr"><input name="title" placeholder="Кітап атауы" required></div>' +
      '<div class="adm-fr"><input name="author" placeholder="Авторы"></div>' +
      '<div class="adm-fr">' +
        '<select name="lang"><option value="kk">қазақша</option>' +
          '<option value="ru">орысша</option><option value="en">ағылшынша</option></select>' +
        '<input name="est_pages" type="number" placeholder="бет" min="0" style="max-width:90px">' +
      '</div>' +
      '<div class="adm-fr"><input name="genre" placeholder="Жанр (мыс. Қазақ халық ертегілері)"></div>' +
      '<div class="adm-fr"><textarea name="source_note" rows="2" required ' +
        'placeholder="❗Құқықтық негіз + дереккөз (міндетті)"></textarea></div>' +
      '<div class="adm-fr"><input type="file" name="file" accept=".epub" required></div>' +
      '<button class="adm-btn" type="submit">Қорға қосу</button>' +
      '<div class="adm-msg" id="admLibMsg"></div>' +
    '</form>' +
    '<div class="adm-libstat">' + Object.entries(byGenre)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([g, arr]) => `<span class="adm-chip">${esc(g)} · ${arr.length}</span>`).join('') +
    '</div>' +
    '<div class="adm-liblist">' + books.slice(0, 40).map(b =>
      `<div class="adm-libr"><b>${esc(b.title)}</b>` +
      `<span>${esc(b.author || '')} · ${b.est_pages || 0} бет · ${esc(b.lang || '')}</span></div>`).join('') +
      (books.length > 40 ? `<div class="admin-empty">…тағы ${books.length - 40}</div>` : '') +
    '</div>';

  const f = document.getElementById('admLibForm');
  f.onsubmit = async (e) => {
    e.preventDefault();
    const msg = document.getElementById('admLibMsg');
    const fd = new FormData(f);
    if (!(fd.get('source_note') || '').trim()) { msg.textContent = 'Құқықтық негіз міндетті'; return; }
    msg.textContent = 'Жүктелуде…';
    try {
      const r = await fetch('/api/admin/library/upload', { method: 'POST', credentials: 'include', body: fd });
      const j = await r.json();
      if (!r.ok) { msg.textContent = j.detail || 'Қате'; return; }
      msg.textContent = '✅ Қосылды';
      _opened.library = false;
      setTimeout(() => document.querySelector('[data-adm-toggle="library"]').click(), 400);
    } catch (_) { msg.textContent = 'Желі қатесі'; }
  };
}

// Шеңбер ашуға рұқсат — founder: «алдымен келісеміз, сосын береміз».
// Қосылу шектелмейді, тек АШУ.
async function renderCircleAccess() {
  const w = document.getElementById('adminCircleWrap');
  if (!w) return;
  let rows = [];
  try { const d = await api('/api/admin/circle/access'); rows = (d && d.access) || []; } catch (_) {}
  w.innerHTML =
    '<form id="admCirForm" class="adm-form">' +
      '<div class="adm-fr"><input name="telegram_id" type="number" placeholder="Telegram ID" required>' +
        '<select name="kind"><option value="class">сынып</option>' +
          '<option value="family">отбасы</option><option value="all">екеуі де</option></select></div>' +
      '<div class="adm-fr"><input name="note" placeholder="Кім / қай мектеп (белгі)"></div>' +
      '<button class="adm-btn" type="submit">Рұқсат беру</button>' +
      '<div class="adm-msg" id="admCirMsg"></div>' +
    '</form>' +
    (rows.length
      ? '<div class="adm-liblist">' + rows.map(r =>
          '<div class="adm-libr"><b>' + esc(r.name) + '</b><span>' + esc(r.kind) +
          (r.note ? ' · ' + esc(r.note) : '') + ' · ' + esc(r.created_at || '') + '</span>' +
          '<button class="adm-rm" data-cir-rm="' + r.telegram_id + '" data-k="' + esc(r.kind) +
          '">алу</button></div>').join('') + '</div>'
      : '<div class="admin-empty">Әзірге ешкімге берілмеген</div>');

  const post = async (body) => {
    const r = await fetch('/api/admin/circle/access', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return r.ok;
  };
  document.getElementById('admCirForm').onsubmit = async (e) => {
    e.preventDefault();
    const f = e.target, msg = document.getElementById('admCirMsg');
    const ok = await post({ telegram_id: Number(f.telegram_id.value),
                            kind: f.kind.value, note: f.note.value });
    msg.textContent = ok ? '✅ Берілді' : 'Қате';
    if (ok) { _opened.circle = false;
      setTimeout(() => document.querySelector('[data-adm-toggle="circle"]').click(), 300); }
  };
  w.querySelectorAll('[data-cir-rm]').forEach(b => {
    b.onclick = async () => {
      if (!confirm('Рұқсатты аламыз ба?')) return;
      await post({ telegram_id: Number(b.dataset.cirRm), kind: b.dataset.k, remove: true });
      _opened.circle = false;
      document.querySelector('[data-adm-toggle="circle"]').click();
      setTimeout(() => document.querySelector('[data-adm-toggle="circle"]').click(), 200);
    };
  });
}

async function renderRecentBlock() {
  const w = document.getElementById('adminRecentWrap');
  if (!w) return;
  w.innerHTML =
    '<div class="admin-filter">' + filterBtn('all', 'Барлығы') +
    filterBtn('user', 'Пайдаланушы') + filterBtn('bot', 'Бот') + '</div>' +
    '<div id="adminListWrap"><div class="loading"><span class="spinner"></span>Жүктелуде...</div></div>';
  await fetchAndRenderRecent();
}

export async function loadAdmin() {
  const el = document.getElementById('adminContent');
  if (!el) return;
  el.innerHTML = '<div id="admQueue"><div class="loading"><span class="spinner"></span>Жүктелуде…</div></div>' +
    SECTIONS.map(s =>
      `<div class="adm-sec" data-adm="${s.key}">
         <button class="adm-head" data-adm-toggle="${s.key}">
           <span class="adm-ic">${s.icon}</span>
           <span class="adm-t">${s.title}</span>
           <span class="adm-n" data-adm-badge="${s.key}"></span>
           <span class="adm-ch">▾</span>
         </button>
         <div class="adm-body" id="${s.wrap}" hidden></div>
       </div>`).join('');

  el.querySelectorAll('[data-adm-toggle]').forEach(b => {
    b.onclick = async () => {
      const key = b.dataset.admToggle;
      const s = SECTIONS.find(x => x.key === key);
      const body = document.getElementById(s.wrap);
      const open = body.hasAttribute('hidden');
      body.toggleAttribute('hidden', !open);
      b.querySelector('.adm-ch').textContent = open ? '▴' : '▾';
      if (open && !_opened[key]) {
        _opened[key] = true;
        body.innerHTML = '<div class="loading"><span class="spinner"></span>Жүктелуде…</div>';
        try { await s.render(); } catch (_) { body.innerHTML = '<div class="admin-empty">Қате шықты</div>'; }
      }
    };
  });
  await renderQueue();
}

async function renderQueue() {
  const host = document.getElementById('admQueue');
  if (!host) return;
  let d = null;
  try { d = await api('/api/admin/queue'); } catch (_) {}
  if (!d || !d.ok) { host.innerHTML = ''; return; }
  const items = (d.queue || []).filter(q => q.n > 0);
  const badge = (k, n) => {
    const el = document.querySelector(`[data-adm-badge="${k}"]`);
    if (el) el.textContent = n > 0 ? n : '';
  };
  (d.queue || []).forEach(q => badge(q.key, q.n));
  badge('library', d.library || 0);

  const today = d.today || {};
  host.innerHTML =
    `<div class="adm-today">
       <div><b>${today.readers || 0}</b><span>бүгін оқыды</span></div>
       <div><b>${Math.round((today.minutes || 0) / 60)} сағ</b><span>бүгін оқылды</span></div>
       <div><b>${d.library || 0}</b><span>қордағы кітап</span></div>
     </div>` +
    (items.length
      ? `<div class="adm-q">${items.map(q =>
          `<button class="adm-qi" data-adm-jump="${q.key}">
             <span>${q.icon} ${q.label}</span><b>${q.n}</b></button>`).join('')}</div>`
      : '<div class="adm-clean">✅ Бәрі таза — растауды күтіп тұрған ештеңе жоқ</div>');

  host.querySelectorAll('[data-adm-jump]').forEach(b => {
    b.onclick = () => {
      const t = document.querySelector(`[data-adm-toggle="${b.dataset.admJump}"]`);
      if (t) { t.click(); t.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    };
  });
}

// ── Мектептер — растау кезегі (01.09) ──────────────────────────
// Мұғалім сынып ашқанда мектеп өзі тіркеледі (verified=0). Расталған мектеп
// қана Рейтингтегі лига тизерінде көрінеді — бұл фейкке қарсы жалғыз сүзгі,
// сондықтан растау осында, founder қолында.
async function renderSchoolsAdmin() {
  const wrap = document.getElementById('adminSchoolsWrap');
  if (!wrap) return;
  let d = null;
  try { d = await api('/api/admin/schools'); } catch (_) {}
  const list = (d && d.schools) || [];
  if (!list.length) {
    wrap.innerHTML = '<div class="admin-empty">Әзірге тіркелген мектеп жоқ. Мұғалім сынып ашқанда осында түседі.</div>';
    return;
  }
  wrap.innerHTML =
    '<div style="font-size:12px;color:var(--ink3);margin-bottom:8px">Расталған мектеп қана лига тизерінде көрінеді. Өшіру сыныптарға тимейді — тек мектеп жазбасын алады.</div>' +
    list.map((s) => {
      const ok = Number(s.verified) === 1;
      return '<div style="background:var(--card);border-radius:10px;padding:10px 12px;margin-bottom:7px;' +
          (ok ? '' : 'border-left:3px solid #C9A96E;') + '">' +
        '<div style="display:flex;justify-content:space-between;gap:8px;align-items:center">' +
          '<div><b>' + esc(s.name) + '</b>' +
            (s.city ? ' <span style="color:var(--ink3);font-size:12px">· ' + esc(s.city) + '</span>' : '') + '</div>' +
          '<span style="font-size:11px;color:' + (ok ? '#3A5C3C' : '#A54A36') + '">' +
            (ok ? '✅ расталған' : '⏳ күтуде') + '</span>' +
        '</div>' +
        '<div style="font-size:12px;color:var(--ink2);margin-top:3px">' +
          s.classes + ' сынып · ' + s.members + ' мүше' +
          (s.teacher ? ' · мұғалім: ' + esc(s.teacher) : '') + '</div>' +
        '<div style="display:flex;gap:6px;margin-top:7px;flex-wrap:wrap">' +
          (ok
            ? '<button class="admin-open" data-sch-act="unverify" data-sch-id="' + s.id + '" style="font-size:11px;padding:5px 10px">Тизерден алу</button>'
            : '<button class="admin-open" data-sch-act="verify" data-sch-id="' + s.id + '" style="font-size:11px;padding:5px 10px;background:#3A5C3C;color:#fff">✅ Растау</button>') +
          '<button class="admin-open" data-sch-act="delete" data-sch-id="' + s.id + '" style="font-size:11px;padding:5px 10px;color:#A54A36">Өшіру</button>' +
        '</div>' +
      '</div>';
    }).join('');
  wrap.querySelectorAll('[data-sch-act]').forEach((b) => {
    b.onclick = async () => {
      const act = b.dataset.schAct;
      if (act === 'delete' && !confirm('Мектеп жазбасын өшіреміз бе? Сыныптар мен оқу дерегі САҚТАЛАДЫ — тек мектеп аты кетеді.')) return;
      b.disabled = true;
      try {
        await fetch('/api/admin/school-set', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(b.dataset.schId), action: act }),
        });
      } catch (_) {}
      await renderSchoolsAdmin();
    };
  });
}

// ── Блогерлер (реф жүйесі) — қосу + статистика + басқару ──
const _blInput = 'padding:9px;border-radius:8px;border:1px solid var(--ink2);background:var(--bg);color:var(--ink);font-size:13px;width:100%;box-sizing:border-box';

function bloggerRow(b) {
  const isCash = b.reward_type === 'cash';
  const stats = `Клик ${b.clicks} · Тіркелу ${b.conversions}` + (isCash ? ` · Табыс ${b.earnings}₸` : '');
  const dot = b.status === 'active' ? '🟢' : '⏸️';
  return '<div style="background:var(--card);border-radius:10px;padding:10px 12px;margin-bottom:7px">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:8px">' +
      '<div><b>' + esc(b.name || b.code) + '</b> <span style="color:var(--ink3);font-size:12px">/' + esc(b.code) + '</span> ' + dot + '</div>' +
      '<div style="font-size:11px;color:var(--ink3)">' + (isCash ? (b.commission + '₸') : 'perk') + '</div>' +
    '</div>' +
    '<div style="font-size:12px;color:var(--ink2);margin-top:3px">' + stats + '</div>' +
    '<div style="font-family:monospace;font-size:11px;color:var(--ink3);margin-top:4px;word-break:break-all">' + esc(b.link) + '</div>' +
    '<div style="display:flex;gap:6px;margin-top:7px;flex-wrap:wrap">' +
      '<button class="admin-open" data-bl-copy="' + esc(b.link) + '" style="font-size:11px;padding:5px 10px">Сілтемені көшіру</button>' +
      '<button class="admin-open" data-bl-status="' + b.id + '" data-bl-cur="' + b.status + '" style="font-size:11px;padding:5px 10px">' + (b.status === 'active' ? 'Тоқтату' : 'Қосу') + '</button>' +
      (!isCash ? '<button class="admin-open" data-bl-promote="' + b.id + '" style="font-size:11px;padding:5px 10px">Cash-қа көтеру</button>' : '') +
    '</div>' +
  '</div>';
}

async function renderBloggers() {
  const wrap = document.getElementById('adminBloggerWrap');
  if (!wrap) return;
  let d = null;
  try { d = await api('/api/admin/bloggers'); } catch {}
  const list = (d && d.bloggers) || [];
  const rows = list.length ? list.map(bloggerRow).join('')
    : '<div style="font-size:12px;color:var(--ink2);opacity:.7;margin:4px 0 0">Әзірге блогер жоқ.</div>';
  wrap.innerHTML =
    '<div style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#7c3aed;font-weight:700;margin:0 0 8px">🔗 Блогерлер (реф жүйесі)</div>' +
    '<div style="background:var(--card);border-radius:12px;padding:12px;margin-bottom:10px;display:flex;flex-direction:column;gap:8px">' +
      '<input id="blCode" placeholder="Код — латын/сан (мыс. aru)" style="' + _blInput + '">' +
      '<input id="blName" placeholder="Аты (мыс. Аружан)" style="' + _blInput + '">' +
      '<div style="display:flex;gap:8px">' +
        '<select id="blType" style="' + _blInput + '"><option value="cash">Cash (ақша)</option><option value="perk">Perk (сыйлық)</option></select>' +
        '<input id="blComm" type="number" value="200" placeholder="Комиссия ₸" style="' + _blInput + '">' +
      '</div>' +
      '<input id="blTg" type="number" placeholder="Telegram ID — кабинет көру үшін (міндетті емес)" style="' + _blInput + '">' +
      '<button id="blAddBtn" class="admin-open" style="background:#7c3aed;color:#fff;border:none">+ Блогер қосу</button>' +
      '<div id="blMsg" style="font-size:12px;min-height:14px;line-height:1.4"></div>' +
    '</div>' +
    '<div id="blList">' + rows + '</div>' +
    '<div style="height:20px"></div>';

  const msg = wrap.querySelector('#blMsg');
  wrap.querySelector('#blAddBtn').onclick = async (ev) => {
    const code = wrap.querySelector('#blCode').value.trim();
    const name = wrap.querySelector('#blName').value.trim();
    const reward_type = wrap.querySelector('#blType').value;
    const commission = parseInt(wrap.querySelector('#blComm').value, 10) || 0;
    const tg = wrap.querySelector('#blTg').value.trim();
    if (!code) { msg.style.color = '#c0392b'; msg.textContent = 'Код керек'; return; }
    ev.target.disabled = true; msg.style.color = 'var(--ink2)'; msg.textContent = 'Қосылуда...';
    const body = { code, name, reward_type, commission };
    if (tg) body.telegram_id = parseInt(tg, 10);
    const r = await api('/api/admin/bloggers', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    ev.target.disabled = false;
    if (r && r.ok) { msg.style.color = '#16a34a'; msg.textContent = '✓ Қосылды: ' + r.link; renderBloggers(); }
    else { msg.style.color = '#c0392b'; msg.textContent = r && r.detail ? r.detail : 'Қосылмады (код бар ма?)'; }
  };

  if (!wrap._blBound) {
    wrap._blBound = true;
    wrap.addEventListener('click', async (e) => {
      const cp = e.target.closest('[data-bl-copy]');
      if (cp) { try { await navigator.clipboard.writeText(cp.dataset.blCopy); cp.textContent = '✓'; setTimeout(() => cp.textContent = 'Сілтемені көшіру', 1200); } catch {} return; }
      const st = e.target.closest('[data-bl-status]');
      if (st) {
        const next = st.dataset.blCur === 'active' ? 'paused' : 'active';
        await api('/api/admin/bloggers/' + st.dataset.blStatus + '/status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: next }) });
        renderBloggers(); return;
      }
      const pr = e.target.closest('[data-bl-promote]');
      if (pr) {
        await api('/api/admin/bloggers/' + pr.dataset.blPromote + '/promote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ commission: 200 }) });
        renderBloggers(); return;
      }
    });
  }
}

// ── Stage 10: Telegram бот квиз нәтижесін жүктеу (CSV) ──
async function renderQuizUpload() {
  const wrap = document.getElementById('adminQuizUploadWrap');
  if (!wrap) return;
  let up = null;
  try { up = await api('/api/admin/marathon/quiz/uploaded'); } catch {}
  const cur = (up && up.count)
    ? `<div style="font-size:12px;color:var(--ink2);margin:6px 0 10px">Қазір жүктелген: <b>${up.count}</b> · сәйкестендірілген: <b>${up.matched}</b></div>` : '';
  wrap.innerHTML =
    '<div style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#ea580c;font-weight:700;margin:0 0 8px">🏁 Квиз нәтижесін жүктеу (бот CSV)</div>' +
    '<div style="font-size:11.5px;color:var(--ink2);opacity:.78;margin-bottom:8px;line-height:1.5">Telegram бот квизінің CSV файлын жүкте. Username бойынша сайт юзерлерімен сәйкестендіріледі → топ-3 чемпион + сертификат автоматты дайын.</div>' +
    cur +
    '<input type="file" id="quizCsv" accept=".csv,text/csv" style="font-size:13px;margin-bottom:8px;display:block;max-width:100%">' +
    '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px"><input type="number" id="quizTotalQ" min="1" placeholder="Сұрақ саны (20)" style="width:150px;padding:8px;border-radius:8px;border:1px solid var(--ink2);background:var(--bg);color:var(--ink);font-size:13px"><button id="quizUpBtn" class="admin-open" style="background:#ea580c;color:#fff;border:none">Жүктеу</button></div>' +
    '<div id="quizUpMsg" style="font-size:12px;min-height:16px;line-height:1.5;margin-bottom:22px"></div>';
  const fileEl = wrap.querySelector('#quizCsv');
  const msg = wrap.querySelector('#quizUpMsg');
  wrap.querySelector('#quizUpBtn').onclick = async (ev) => {
    const f = fileEl.files && fileEl.files[0];
    if (!f) { msg.style.color = '#c0392b'; msg.textContent = 'Алдымен файл таңда'; return; }
    ev.target.disabled = true; msg.style.color = 'var(--ink2)'; msg.textContent = 'Жүктелуде...';
    try {
      const fd = new FormData();
      fd.append('file', f);
      const tq = wrap.querySelector('#quizTotalQ').value;
      if (tq) fd.append('total_q', tq);
      const r = await fetch('/api/admin/marathon/quiz/upload', { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        const champs = (d.champions || []).map(c => `${c.rank}. ${esc(c.name || c.username || '')} (${c.correct})`).join(' · ');
        msg.style.color = '#2e9e5b';
        msg.innerHTML = `✓ ${d.total} нәтиже · сәйкес ${d.matched} · сәйкессіз ${d.unmatched}` + (champs ? `<br>🏆 ${champs}` : '');
        toast('Нәтиже жүктелді');
      } else { msg.style.color = '#c0392b'; msg.textContent = 'Қате: ' + (d.detail || r.status); }
    } catch { msg.style.color = '#c0392b'; msg.textContent = 'Желі қатесі'; }
    ev.target.disabled = false;
  };
}

// ── Stage 10: Квиз сұрақтарын енгізу (paste формат) ──
function parseQuizText(text) {
  const blocks = String(text || '').split(/\n\s*\n+/).map(b => b.trim()).filter(Boolean);
  const out = [], errs = [];
  blocks.forEach((b, bi) => {
    const lines = b.split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;
    const qtext = lines[0].replace(/^\d+[).]\s*/, '').trim();
    const opts = []; let correct = -1;
    lines.slice(1).forEach(l => {
      if (!/^[-–—•]/.test(l)) return;
      let o = l.replace(/^[-–—•]+\s*/, '');
      if (/\s*\*\s*$/.test(o)) { correct = opts.length; o = o.replace(/\s*\*\s*$/, '').trim(); }
      if (o) opts.push(o);
    });
    if (!qtext || opts.length < 2) { errs.push(`${bi + 1}-блок: сұрақ/нұсқа жетіспейді`); return; }
    if (correct < 0) { errs.push(`${bi + 1}-блок: дұрыс нұсқа «*» белгіленбеген`); return; }
    out.push({ text: qtext, options: opts, correct });
  });
  return { questions: out, errs };
}

async function renderQuizAdmin() {
  const wrap = document.getElementById('adminQuizWrap');
  if (!wrap) return;
  let data;
  try { data = await api('/api/admin/marathon/quiz/questions'); }
  catch {
    wrap.innerHTML = '<div style="font-size:12.5px;color:#c0392b;margin-bottom:14px">📝 Квиз сұрақтарын жүктеу қатесі — <button data-quiz-retry style="font-size:12px;color:#ea580c;background:rgba(234,88,12,.08);border:1px solid rgba(234,88,12,.3);border-radius:7px;padding:4px 10px;cursor:pointer">қайта көру</button></div>';
    wrap.querySelector('[data-quiz-retry]')?.addEventListener('click', renderQuizAdmin);
    return;
  }
  const qs = (data && data.questions) || [];
  const numTarget = (data && data.num_q) || 20;
  const pre = qs.map(q => q.text + '\n' + q.options.map((o, i) => '- ' + o + (i === q.correct ? ' *' : '')).join('\n')).join('\n\n');
  wrap.innerHTML =
    '<div style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#ea580c;font-weight:700;margin:0 0 8px">📝 Квиз сұрақтары (' + qs.length + '/' + numTarget + ')</div>' +
    '<div style="font-size:11.5px;color:var(--ink2);opacity:.78;margin-bottom:8px;line-height:1.5">Әр сұрақты <b>бос жолмен</b> бөл. Нұсқалар «<b>- </b>» -ден басталады. Дұрыс нұсқаның соңына «<b>*</b>» қой.</div>' +
    '<textarea id="quizTa" rows="12" spellcheck="false" style="width:100%;box-sizing:border-box;font-family:monospace;font-size:12.5px;line-height:1.5;padding:10px;border-radius:10px;border:1px solid var(--ink2);background:var(--bg);color:var(--ink);resize:vertical">' + esc(pre) + '</textarea>' +
    '<div id="quizMsg" style="font-size:12px;margin:8px 0;min-height:16px"></div>' +
    '<div style="display:flex;gap:8px;align-items:center;margin-bottom:22px">' +
      '<button id="quizSave" class="admin-open" style="background:#ea580c;color:#fff;border:none">Сақтау</button>' +
      '<button id="quizCheck" style="font-size:12px;background:var(--bg);border:1px solid var(--ink2);border-radius:8px;padding:7px 12px;cursor:pointer;color:var(--ink)">Тексеру</button>' +
    '</div>';
  const ta = wrap.querySelector('#quizTa');
  const msg = wrap.querySelector('#quizMsg');
  wrap.querySelector('#quizCheck').onclick = () => {
    const { questions, errs } = parseQuizText(ta.value);
    msg.style.color = errs.length ? '#c0392b' : '#2e9e5b';
    msg.textContent = errs.length ? errs.join(' · ') : (questions.length + ' сұрақ дұрыс талданды ✓');
  };
  wrap.querySelector('#quizSave').onclick = async (ev) => {
    const { questions, errs } = parseQuizText(ta.value);
    if (errs.length) { msg.style.color = '#c0392b'; msg.textContent = errs.join(' · '); return; }
    if (!questions.length) { msg.style.color = '#c0392b'; msg.textContent = 'Сұрақ жоқ'; return; }
    ev.target.disabled = true;
    try {
      const r = await fetch('/api/admin/marathon/quiz/questions', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ questions }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { msg.style.color = '#2e9e5b'; msg.textContent = '✓ ' + (d.saved || questions.length) + ' сұрақ сақталды'; toast('Квиз сақталды'); }
      else { msg.style.color = '#c0392b'; msg.textContent = 'Қате: ' + (d.detail || r.status); }
    } catch { msg.style.color = '#c0392b'; msg.textContent = 'Желі қатесі'; }
    ev.target.disabled = false;
  };
}

// ── Stage 10: Марафон тіркеулері (сайт ішілік) ──
async function renderMarathonOrders() {
  const wrap = document.getElementById('adminMarathonWrap');
  if (!wrap) return;
  let data;
  try { data = await api('/api/admin/marathon/pending'); }
  catch {
    wrap.innerHTML = '<div style="font-size:12.5px;color:#c0392b;margin-bottom:14px">🏁 Марафон тіркеулерін жүктеу қатесі — <button data-mr-retry style="font-size:12px;color:#ea580c;background:rgba(234,88,12,.08);border:1px solid rgba(234,88,12,.3);border-radius:7px;padding:4px 10px;cursor:pointer">қайта көру</button></div>';
    wrap.querySelector('[data-mr-retry]')?.addEventListener('click', renderMarathonOrders);
    return;
  }
  const orders = (data && data.orders) || [];
  const head = '<div style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#ea580c;font-weight:700;margin:0 0 10px">🏁 Марафон тіркеулері' + (orders.length ? ' (' + orders.length + ')' : '') + '</div>';
  if (!orders.length) { wrap.innerHTML = head + '<div style="font-size:13px;color:var(--ink2);opacity:.7;margin-bottom:18px">Күтудегі тіркеу жоқ</div>'; return; }
  wrap.innerHTML = head + '<div class="admin-list">' + orders.map(o =>
    '<div class="admin-row" data-mr-row="' + o.id + '">' +
      '<div class="admin-row-main">' +
        '<div class="admin-row-title">' + esc(o.name) + '</div>' +
        '<div class="admin-row-meta">' + o.amount + '₸ · ' + esc((o.created_at || '').slice(0, 16)) + (o.username ? ' · @' + esc(o.username) : '') + '</div>' +
        '<button data-mr-receipt="' + o.id + '" style="margin-top:6px;font-size:12px;color:#ea580c;background:rgba(234,88,12,.08);border:1px solid rgba(234,88,12,.3);border-radius:8px;padding:5px 11px;cursor:pointer">🧾 Чекті көру</button>' +
      '</div>' +
      '<div class="admin-row-actions">' +
        '<button class="admin-open" data-mr-approve="' + o.id + '" style="background:#ea580c;color:#fff;border:none">✓ Тіркеу</button>' +
        '<button class="admin-delete" data-mr-reject="' + o.id + '">Бас тарту</button>' +
      '</div></div>'
  ).join('') + '</div><div style="height:20px"></div>';
  wrap.querySelectorAll('[data-mr-receipt]').forEach(b => b.onclick = () => showReceipt(b.dataset.mrReceipt, 'marathon'));
  wrap.querySelectorAll('[data-mr-approve]').forEach(b => b.onclick = () => marathonApprove(b.dataset.mrApprove, b));
  wrap.querySelectorAll('[data-mr-reject]').forEach(b => b.onclick = () => marathonReject(b.dataset.mrReject, b));
}

async function marathonApprove(id, btn) {
  if (!window.confirm('Марафонға тіркеуді растайсың ба?')) return;
  if (btn) btn.disabled = true;
  try {
    const r = await fetch('/api/admin/marathon/' + id + '/approve', { method: 'POST', credentials: 'include' });
    if (r.ok) { toast('✓ Тіркелді'); document.querySelector('[data-mr-row="' + id + '"]')?.remove(); }
    else { const e = await r.json().catch(() => ({})); toast('Қате: ' + (e.detail || r.status)); if (btn) btn.disabled = false; }
  } catch { toast('Желі қатесі'); if (btn) btn.disabled = false; }
}

async function marathonReject(id, btn) {
  const reason = window.prompt('Бас тарту себебі (міндетті емес):');
  if (reason === null) return;
  if (btn) btn.disabled = true;
  try {
    const r = await fetch('/api/admin/marathon/' + id + '/reject', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: reason }),
    });
    if (r.ok) { toast('Бас тартылды'); document.querySelector('[data-mr-row="' + id + '"]')?.remove(); }
    else if (btn) btn.disabled = false;
  } catch { if (btn) btn.disabled = false; }
}

// ── Stage 9: Жан дос тапсырыстары (премиум) ──
async function renderPremiumOrders() {
  const wrap = document.getElementById('adminPremiumWrap');
  if (!wrap) return;
  let data;
  try { data = await api('/api/admin/premium/pending'); }
  catch {
    wrap.innerHTML = '<div style="font-size:12.5px;color:#c0392b;margin-bottom:14px">🕯 Жан дос тапсырыстарын жүктеу қатесі — <button data-pm-retry style="font-size:12px;color:var(--ac);background:var(--acl);border:1px solid var(--ac2);border-radius:7px;padding:4px 10px;cursor:pointer">қайта көру</button></div>';
    wrap.querySelector('[data-pm-retry]')?.addEventListener('click', renderPremiumOrders);
    return;
  }
  const orders = (data && data.orders) || [];
  const head = '<div style="font-size:11px;letter-spacing:1px;text-transform:uppercase;color:var(--ac);font-weight:700;margin:0 0 10px">🕯 Жан дос тапсырыстары' + (orders.length ? ' (' + orders.length + ')' : '') + '</div>';
  if (!orders.length) { wrap.innerHTML = head + '<div style="font-size:13px;color:var(--ink2);opacity:.7;margin-bottom:18px">Күтудегі төлем жоқ</div>'; return; }
  wrap.innerHTML = head + '<div class="admin-list">' + orders.map(o =>
    '<div class="admin-row" data-pm-row="' + o.id + '">' +
      '<div class="admin-row-main">' +
        '<div class="admin-row-title">' + esc(o.name) + '</div>' +
        '<div class="admin-row-meta">' + o.amount + '₸ · ' + (o.months || 1) + ' ай · ' + esc((o.created_at || '').slice(0, 16)) + (o.username ? ' · @' + esc(o.username) : '') + '</div>' +
        '<button data-pm-receipt="' + o.id + '" style="margin-top:6px;font-size:12px;color:var(--ac);background:var(--acl);border:1px solid var(--ac2);border-radius:8px;padding:5px 11px;cursor:pointer">🧾 Чекті көру</button>' +
      '</div>' +
      '<div class="admin-row-actions">' +
        '<button class="admin-open" data-pm-approve="' + o.id + '" style="background:#4A5D3A;color:#fff;border:none">✓ Растау</button>' +
        '<button class="admin-delete" data-pm-reject="' + o.id + '">Бас тарту</button>' +
      '</div></div>'
  ).join('') + '</div><div style="height:20px"></div>';
  wrap.querySelectorAll('[data-pm-receipt]').forEach(b => b.onclick = () => showReceipt(b.dataset.pmReceipt));
  wrap.querySelectorAll('[data-pm-approve]').forEach(b => b.onclick = () => premiumApprove(b.dataset.pmApprove, b));
  wrap.querySelectorAll('[data-pm-reject]').forEach(b => b.onclick = () => premiumReject(b.dataset.pmReject, b));
}

// Чекті сайттың ішінде көрсету (жаңа бет емес — × бассаң админ панельге қайтасың)
// kind: 'premium' | 'marathon'
function showReceipt(orderId, kind = 'premium') {
  const url = '/api/admin/' + kind + '/receipt/' + orderId;
  const old = document.getElementById('pmReceiptOv');
  if (old) old.remove();
  const ov = document.createElement('div');
  ov.id = 'pmReceiptOv';
  ov.style.cssText = 'position:fixed;inset:0;z-index:400;background:rgba(0,0,0,.85);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;gap:14px';
  ov.innerHTML =
    '<button id="pmRcptClose" aria-label="Жабу" style="position:absolute;top:14px;right:16px;width:42px;height:42px;border-radius:50%;border:none;background:rgba(255,255,255,.18);color:#fff;font-size:26px;cursor:pointer;line-height:1">×</button>' +
    '<img src="' + url + '" alt="чек" style="max-width:100%;max-height:78vh;border-radius:10px;object-fit:contain;background:#fff" ' +
      'onerror="this.style.display=\'none\';var f=document.getElementById(\'pmRcptFallback\');if(f)f.style.display=\'inline-block\'">' +
    '<a id="pmRcptFallback" href="' + url + '" target="_blank" rel="noopener" style="display:none;color:#fff;text-decoration:underline;font-size:14px;padding:10px">📄 Чекті бөлек ашу</a>' +
    '<div style="color:rgba(255,255,255,.65);font-size:12px">Жабу үшін × немесе фонды бас</div>';
  document.body.appendChild(ov);
  document.body.style.overflow = 'hidden';
  const close = () => { ov.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', esc); };
  const esc = (e) => { if (e.key === 'Escape') close(); };
  ov.querySelector('#pmRcptClose').onclick = close;
  ov.onclick = (e) => { if (e.target === ov) close(); };
  document.addEventListener('keydown', esc);
}

async function premiumApprove(id, btn) {
  if (!window.confirm('Жан дос беруді растайсың ба?')) return;
  if (btn) btn.disabled = true;
  try {
    const r = await fetch('/api/admin/premium/' + id + '/approve', { method: 'POST', credentials: 'include' });
    if (r.ok) { toast('✓ Жан дос берілді'); document.querySelector('[data-pm-row="' + id + '"]')?.remove(); }
    else { const e = await r.json().catch(() => ({})); toast('Қате: ' + (e.detail || r.status)); if (btn) btn.disabled = false; }
  } catch { toast('Желі қатесі'); if (btn) btn.disabled = false; }
}

async function premiumReject(id, btn) {
  const reason = window.prompt('Бас тарту себебі (міндетті емес):');
  if (reason === null) return;
  if (btn) btn.disabled = true;
  try {
    const r = await fetch('/api/admin/premium/' + id + '/reject', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: reason }),
    });
    if (r.ok) { toast('Бас тартылды'); document.querySelector('[data-pm-row="' + id + '"]')?.remove(); }
    else if (btn) btn.disabled = false;
  } catch { if (btn) btn.disabled = false; }
}

function filterBtn(value, label) {
  const active = _adminFilter === value;
  return '<button class="admin-filter-btn' + (active ? ' active' : '') +
    '" data-action="admin-filter" data-source="' + value + '">' + esc(label) + '</button>';
}

export async function setAdminFilter(source) {
  _adminFilter = source || 'all';
  // Re-render filter row + list.
  document.querySelectorAll('.admin-filter-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.source === _adminFilter);
  });
  await fetchAndRenderRecent();
}

async function fetchAndRenderRecent() {
  const wrap = document.getElementById('adminListWrap');
  if (!wrap) return;
  wrap.innerHTML = '<div class="loading"><span class="spinner"></span>Жүктелуде...</div>';
  try {
    const data = await api('/api/admin/recent-books?source=' + _adminFilter + '&limit=50');
    renderRecent(data.items || []);
  } catch (e) {
    wrap.innerHTML = '<div class="empty-state"><p>Қате: ' + esc(String(e.message || e)) + '</p></div>';
  }
}

function renderRecent(items) {
  const wrap = document.getElementById('adminListWrap');
  if (!wrap) return;
  if (!items.length) {
    wrap.innerHTML = '<div class="empty-state"><p>Кітап табылмады.</p></div>';
    return;
  }
  wrap.innerHTML =
    '<div class="admin-list">' +
    items.map(b => {
      const dt = (b.added_at || '').slice(0, 16).replace('T', ' ');
      const srcLabel = b.source === 'user'
        ? '<span class="admin-src admin-src-user">User</span>'
        : '<span class="admin-src admin-src-bot">Bot</span>';
      const submitterText = b.source === 'user'
        ? esc(b.submitter_name) + (b.submitter_username ? ' (@' + esc(b.submitter_username) + ')' : '')
        : esc(b.submitter_name) + (b.submitter_username ? ' (@' + esc(b.submitter_username) + ')' : '');
      return '<div class="admin-row" data-book-id="' + b.book_id + '">' +
        '<div class="admin-row-main">' +
          '<div class="admin-row-title">' + esc(b.title) + '</div>' +
          '<div class="admin-row-author">' + esc(b.author || '') + '</div>' +
          '<div class="admin-row-meta">' + srcLabel + ' · ' + esc(dt) + ' · ' + submitterText + '</div>' +
        '</div>' +
        '<div class="admin-row-actions">' +
          '<button class="admin-open" data-action="admin-open-book" data-slug="' + esc(b.slug) + '">Кітапты ашу</button>' +
          '<button class="admin-delete" data-action="admin-delete-book" data-book-id="' + b.book_id + '" data-title="' + esc(b.title) + '">Өшіру</button>' +
        '</div>' +
      '</div>';
    }).join('') +
    '</div>';
}

export function adminOpenBook(slug) {
  if (!slug) return;
  if (typeof window.openBookPage === 'function') window.openBookPage(slug);
}

export async function adminDeleteBook(bookId, title, btn) {
  if (!bookId) return;
  // Confirmation — destructive операция.
  const ok = window.confirm('"' + (title || 'кітапты') + '" өшіруге сенімдісіз бе?');
  if (!ok) return;
  if (btn) btn.disabled = true;
  try {
    const r = await fetch('/api/admin/books/' + bookId + '/delete', {
      method: 'POST', credentials: 'include',
    });
    if (r.ok) {
      toast('✓ Кітап өшірілді');
      document.querySelector('.admin-row[data-book-id="' + bookId + '"]')?.remove();
    } else {
      const err = await r.json().catch(() => ({}));
      toast('Қате: ' + (err.detail || r.status));
      if (btn) btn.disabled = false;
    }
  } catch {
    toast('Желі қатесі');
    if (btn) btn.disabled = false;
  }
}
